import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Camera, Database } from 'lucide-react';
import { images } from '../assets/images';
import Section from '../components/Section';
import FeatureCard from '../components/FeatureCard';

const Features: React.FC = () => {
  return (
    <>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary-900 to-primary-800 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            className="text-4xl md:text-5xl font-display font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Exereyes'ın Özellikleri
          </motion.h1>
          <motion.p
            className="text-xl mb-8 max-w-3xl mx-auto text-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            %100 yerli mühendislik ile geliştirilen özgün yapay zeka teknolojimiz, görüntü işleme teknolojisi kullanarak evdeki sıradan eşyaları egzersiz ekipmanına dönüştüren mobil uygulamadır.
          </motion.p>
        </div>
      </section>

      {/* Core Technology Features */}
      <Section
        title="Çekirdek Teknolojilerimiz"
        subtitle="Exereyes'ın temelini oluşturan yenilikçi teknolojiler"
        centered
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <FeatureCard
            title="Ortam Farkındalıklı Nesne Dönüştürme"
            description="Telefon kamerasıyla ortamı tarayarak evdeki sıradan nesneleri (su şişesi, sandalye, kitap) analiz ederek bunları birer spor ekipmanına dönüştürür."
            icon={<Camera size={24} />}
            delay={0.1}
          />
          <FeatureCard
            title="Çift Yönlü Etkileşim ve Yapay Zeka Koçluğu"
            description="3 boyutlu robot eğitmen egzersizi gösterirken, modül kullanıcının formunu takip eder. Sistem kullanıcının performansına göre süreci optimize eder."
            icon={<Brain size={24} />}
            delay={0.2}
          />
          <FeatureCard
            title="Güvenlik ve Kişiselleştirilmiş Sağlık Filtresi"
            description="Kullanıcının sakatlık geçmişini ve fiziksel kısıtlarını analiz ederek 'önce zarar verme' ilkesiyle çalışır. Risk oluşturabilecek hareketleri filtreler."
            icon={<Database size={24} />}
            delay={0.3}
          />
        </div>
      </Section>

      {/* Technical Details */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center">
            <div className="lg:w-1/2 mb-10 lg:mb-0">
              <motion.img
                src={images.featureAI}
                alt="Teknik Özellikler"
                className="rounded-xl shadow-medium mx-auto"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <div className="lg:w-1/2 lg:pl-16">
              <motion.h2
                className="text-3xl md:text-4xl font-display font-bold text-dark mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                Teknik Özellikler
              </motion.h2>
              
              <div className="space-y-6">
                {[
                  {
                    title: "Donanım Bağımsızlığı",
                    description: "Sadece standart bir akıllı telefon kamerası kullanarak, görüntü işleme teknolojisiyle kullanıcının iskelet sistemini modeller. Giriş maliyeti sıfırdır."
                  },
                  {
                    title: "Sıfır Ekipman Politikası",
                    description: "Pahalı spor aletlerine ihtiyaç duymadan, mevcut eşyaların yeniden işlevlendirilmesi ile sürdürülebilir ve sonsuz varyasyonda antrenman deneyimi"
                  },
                  {
                    title: "3 Boyutlu Robot Eğitmen",
                    description: "Kullanıcının kişisel verilerine göre özelleştirilen antrenman programları, üç boyutlu bir robot eğitmen tarafından görsel olarak sunulmaktadır"
                  },
                  {
                    title: "Yapay Zeka Destekli Analiz",
                    description: "Kullanıcının egzersiz süreçlerini ve gelişimini yapay zeka destekli analizlerle takip ederek, kişiye özel bir süreç değerlendirmesi sunar"
                  }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: 0.1 * index }}
                  >
                    <h3 className="text-xl font-semibold text-dark mb-2">{item.title}</h3>
                    <p className="text-dark-light">{item.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rest of the existing sections... */}
    </>
  );
};

export default Features;